import React, { useState, useEffect } from 'react';
import ComposeNotification from './components/ComposeNotification';
import NotificationHistory from './components/NotificationHistory';
import NotificationModal from './components/NotificationModal';
import useSuperAdminMarketingStore from '@/stores/SuperAdminStore/SuperAdminMarketingStore';
import { NotificationRecord } from '@/services/PagesServices/SuperAdminMarketingService';
import './MarketingAndNotification.css';

const MarketingAndNotification: React.FC = () => {
  const [activeTab, setActiveTab] = useState('compose');
  const [selectedNotification, setSelectedNotification] = useState<NotificationRecord | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editSeed, setEditSeed] = useState<NotificationRecord | null>(null);

  const {
    audiences,
    notificationHistory,
    fetchAudiences,
    fetchNotificationHistory,
  } = useSuperAdminMarketingStore();

  // Lazy load data based on active tab
  useEffect(() => {
    if (activeTab === 'compose' && !audiences) {
      fetchAudiences();
    }
    if (activeTab === 'history' && !notificationHistory) {
      fetchNotificationHistory();
    }
  }, [activeTab, audiences, notificationHistory, fetchAudiences, fetchNotificationHistory]);

  const handleViewNotification = (notification: NotificationRecord) => {
    setSelectedNotification(notification);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedNotification(null);
  };

  const handleEditNotification = (notification: NotificationRecord) => {
    setEditSeed(notification);
    setShowModal(false);
    setActiveTab('compose');
  };

  return (
    <div className="s-admin-marketing-container">
      <div className="s-admin-marketing-breadcrumb">
        <span className="s-admin-marketing-breadcrumb-item">Tools</span>
        <span className="s-admin-marketing-breadcrumb-separator">›</span>
        <span className="s-admin-marketing-breadcrumb-item s-admin-marketing-breadcrumb-active">Marketing</span>
      </div>

      <div className="s-admin-marketing-header">
        <h1 className="s-admin-marketing-title">Tools</h1>
        <p className="s-admin-marketing-subtitle">s-administrative utilities and system tools</p>
      </div>

      <div className="s-admin-marketing-tabs">
        <button
          className={`s-admin-marketing-tab ${activeTab === 'compose' ? 's-admin-marketing-tab-active' : ''}`}
          onClick={() => setActiveTab('compose')}
        >
          Compose Notification
        </button>
        <button
          className={`s-admin-marketing-tab ${activeTab === 'history' ? 's-admin-marketing-tab-active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Notification History
        </button>
        <button
          className={`s-admin-marketing-tab ${activeTab === 'tags' ? 's-admin-marketing-tab-active' : ''}`}
          onClick={() => setActiveTab('tags')}
        >
          Audience Tags
        </button>
      </div>

      {activeTab === 'compose' && (
        <div className="s-admin-marketing-content">
          <ComposeNotification
            editSeed={editSeed}
            onEditConsumed={() => setEditSeed(null)}
          />
        </div>
      )}

      {activeTab === 'history' && (
        <div className="s-admin-marketing-content">
          <NotificationHistory
            onViewNotification={handleViewNotification}
            onNewNotification={() => { setEditSeed(null); setActiveTab('compose'); }}
            onEditNotification={handleEditNotification}
          />
        </div>
      )}

      {activeTab === 'tags' && (
        <div className="s-admin-marketing-content">
          <h2 className="s-admin-marketing-section-title">Audience Tags</h2>
          <p className="s-admin-marketing-section-subtitle">Manage and organize your audience segments</p>
          {/* Add Audience Tags content here */}
        </div>
      )}

      {showModal && (
        <NotificationModal
          notification={selectedNotification}
          onClose={handleCloseModal}
          onEdit={handleEditNotification}
        />
      )}
    </div>
  );
};

export default MarketingAndNotification;
